import { useQuery } from '@apollo/client/react'
import {
  PublicBusinessStatsDocument,
  type PublicBusinessStatsQuery,
} from '@/graphql/generated/graphql'

type PublicBusinessItem =
  PublicBusinessStatsQuery['photos']['items'][number]

/** Negocio con logo garantizado (para los avatares del hero). */
export interface PublicBusinessPhoto {
  id: string
  legalName: string
  logo: string
}

/**
 * Estadística PÚBLICA de negocios para el hero del landing (sin auth): el número
 * exacto de negocios activos de Zeus (`totalItems`) y los más RECIENTES **que
 * tienen foto** para los avatares. Query pública del gateway (`publicBusinesses`,
 * @Auth None).
 *
 * Las fotos las filtra ZEUS (`withLogoOnly`), no el cliente: antes se pedía una
 * ventana de 12 y se descartaban los que no tuvieran logo, así que bastaba con
 * que los doce últimos registrados vinieran sin foto para quedarse sin ninguna.
 * "Los últimos CON foto" solo lo puede responder quien tiene la tabla delante.
 *
 * ⚠️ El número va en una consulta APARTE y SIN filtro: `totalItems` se cuenta con
 * el mismo `where` que los items, así que compartir la consulta filtrada haría
 * que la portada dijera un número más bajo que el real.
 *
 * Tolerante a fallos (§6.6): si el gateway no responde, `totalItems` es null y
 * `photos` va vacío — el hero se pinta entero, sin avatares y sin número.
 */
export function usePublicBusinessStats(photoCount = 3) {
  const { data, loading, error } = useQuery(PublicBusinessStatsDocument, {
    variables: { photoCount },
    fetchPolicy: 'cache-first',
  })

  const totalItems = data?.total.totalItems ?? null
  const photos: PublicBusinessPhoto[] = (data?.photos.items ?? [])
    // Defensa: contra un gateway anterior al filtro, el logo podría venir vacío.
    .filter((b: PublicBusinessItem) => !!b.logo)
    .map((b: PublicBusinessItem) => ({
      id: b.id,
      legalName: b.legalName,
      logo: b.logo ?? '',
    }))

  return { totalItems, photos, loading, error }
}
